import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthForm from "./components/AuthForm";
import Map from "./components/Map";
import Header from "./components/Header";
import Profile from "./components/Profile";
import About from "./components/About";
import Community from "./components/Community";
import ResetPassword from "./components/ResetPassword";
import { supabase } from "./supabaseClient";
import Terms from "./components/Terms";
import Privacy from "./components/Privacy";
import AccountDeletion from "./components/AccountDeletion";
import BottomNav from "./components/BottomNav";
import MyDives from "./components/MyDives";
import ClubPage from "./components/ClubPage";
import { initPush } from "./utils/push";
import PushPrimer from "./components/PushPrimer";
import DesktopNav from "./components/DesktopNav";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { fetchWithAuth } from "./utils/api";
import BadgeCelebration from "./components/BadgeCelebration";

// Sous-composant qui gère le Header et les routes principales
const AppContent = ({ user, handleLogout, filterDate, handleFilterChange }) => {
  const location = useLocation();
  const [canton, setCanton] = useState("");
  const [diveType, setDiveType] = useState("");
  const [gas, setGas] = useState("");
  const [config, setConfig] = useState("");
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [badgeState, setBadgeState] = useState({ keys: [], summary: false });

  // Badges nouvellement débloqués → popup de célébration. Vérifié à l'ouverture de l'app
  // et sur demande (event "badges-check" émis après une action pouvant débloquer un badge).
  useEffect(() => {
    // Pas de célébration de badges sur la page de reset : le lien de réinitialisation
    // crée une session temporaire, ce qui déclenchait le récap à contretemps.
    if (!user?.user?.id || location.pathname === "/reset-password") return;
    const check = async () => {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/badges/new`);
        const data = await res.json();
        if (Array.isArray(data?.newlyEarned) && data.newlyEarned.length) {
          setBadgeState((s) => (s.keys.length ? s : { keys: data.newlyEarned, summary: !!data.firstSync && data.newlyEarned.length > 1 }));
        }
      } catch { /* ignore */ }
    };
    check();
    window.addEventListener("badges-check", check);
    return () => window.removeEventListener("badges-check", check);
  }, [user?.user?.id, location.pathname]);

  // Marque la page courante sur <html> (la carte doit rester sans scroll sur desktop).
  // On cible <html> car en mode standards c'est lui l'élément qui scrolle, pas <body>.
  useEffect(() => {
    document.documentElement.setAttribute("data-page", location.pathname === "/" ? "map" : "content");
    return () => document.documentElement.removeAttribute("data-page");
  }, [location.pathname]);

  // Bouton retour Android : revenir dans l'historique de l'app plutôt que la fermer.
  // À la racine (rien où revenir), on met l'app en arrière-plan au lieu de la quitter.
  useEffect(() => {
    if (!(Capacitor?.isNativePlatform && Capacitor.isNativePlatform())) return;
    let sub;
    CapApp.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.minimizeApp();
      }
    }).then((h) => { sub = h; });
    return () => { if (sub) sub.remove(); };
  }, []);

  return (
    <>
      {/* Barre de nav desktop (>=1024px) — le CSS gère la visibilité.
          Présente aussi sur la carte (le header de la carte glisse dessous). */}
      {location.pathname !== "/reset-password" &&
        location.pathname !== "/cgu" &&
        location.pathname !== "/confidentialite" &&
        location.pathname !== "/suppression-compte" && (
          <DesktopNav onLogout={handleLogout} currentUser={user?.user} />
        )}

      {/* Cache le Header sur /profile, /reset-password, /about, /community */}
      {!location.pathname.startsWith("/profile") &&
        location.pathname !== "/reset-password" &&
        location.pathname !== "/about" &&
        location.pathname !== "/community" &&
        location.pathname !== "/dives" &&
        !location.pathname.startsWith("/clubs") &&
        location.pathname !== "/cgu" &&
        location.pathname !== "/confidentialite" &&
        location.pathname !== "/suppression-compte"
        && (
          <Header
            currentUser={user?.user}
            onLogout={handleLogout}
            filterDate={filterDate}
            onFilterChange={handleFilterChange}
            onCantonChange={(e) => setCanton(e.target.value)}
            onTypeChange={(e) => setDiveType(e.target.value)}
            onGasChange={(e) => setGas(e.target.value)}
            onConfigChange={(e) => setConfig(e.target.value)}
            onFiltersPanelChange={setIsFiltersPanelOpen}
          />
        )}

      <Routes>
        <Route
          path="/"
          element={
            <Map
              currentUser={user?.user}
              filterDate={filterDate}
              filterCanton={canton}
              filterType={diveType}
              filterGas={gas}
              filterConfig={config}
              isFiltersPanelOpen={isFiltersPanelOpen}
            />
          }
        />

        <Route path="/profile" element={<Profile currentUser={user?.user} />} />
        <Route path="/profile/:user_id" element={<Profile currentUser={user?.user} isPublicProfile={true} />} />

        <Route path="/forum" element={<Navigate to="/about" replace />} />
        <Route path="/about" element={<About onLogout={handleLogout} />} />
        <Route path="/community" element={<Community onLogout={handleLogout} currentUser={user?.user} />} />
        <Route path="/dives" element={<MyDives currentUser={user?.user} onLogout={handleLogout} />} />
        <Route path="/clubs/:id" element={<ClubPage currentUser={user?.user} onLogout={handleLogout} />} />
        <Route path="/cgu" element={<Terms />} />
        <Route path="/confidentialite" element={<Privacy />} />
        <Route path="/suppression-compte" element={<AccountDeletion />} />

        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>

      {/* Barre d'onglets mobile (masquée sur les pages edge) */}
      {location.pathname !== "/reset-password" &&
        location.pathname !== "/cgu" &&
        location.pathname !== "/confidentialite" &&
        location.pathname !== "/suppression-compte" && <BottomNav currentUser={user?.user} />}

      <BadgeCelebration queue={badgeState.keys} summary={badgeState.summary} onDone={() => setBadgeState({ keys: [], summary: false })} />
    </>
  );
};

function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [filterDate, setFilterDate] = useState("");
  const navigate = useNavigate();

  // Nouvelle logique : écoute l'événement Supabase PASSWORD_RECOVERY
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session);
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-password", { replace: true });
      }
    });

    // Récupération initiale de session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session);
    });

    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  // Enregistrement aux notifications push (app native uniquement) une fois connecté
  useEffect(() => {
    if (user) initPush();
  }, [user]);

  const handleAuthSuccess = (data) => setUser(data);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      // On conserve les préférences de langue i18n au lieu de tout effacer.
      const lng = localStorage.getItem("i18nextLng");
      const lngManual = localStorage.getItem("i18nextLngManual");
      localStorage.clear();
      sessionStorage.clear();
      if (lng) localStorage.setItem("i18nextLng", lng);
      if (lngManual) localStorage.setItem("i18nextLngManual", lngManual);
      setUser(null);
      window.location.reload();
    } catch (err) {
      console.error("Erreur lors de la déconnexion :", err);
    }
  };

  const handleFilterChange = (valueOrEvent) => {
    if (!valueOrEvent) return setFilterDate("");

    if (valueOrEvent.target) return setFilterDate(valueOrEvent.target.value);

    if (valueOrEvent instanceof Date) {
      const y = valueOrEvent.getFullYear();
      const m = String(valueOrEvent.getMonth() + 1).padStart(2, "0");
      const d = String(valueOrEvent.getDate()).padStart(2, "0");
      return setFilterDate(`${y}-${m}-${d}`);
    }

    if (typeof valueOrEvent === "string") return setFilterDate(valueOrEvent);

    console.warn("Valeur inattendue pour handleFilterChange:", valueOrEvent);
  };

  return (
    <div style={{ position: "relative", fontFamily: "Arial, sans-serif" }}>
      {!user ? (
        <Routes>
          {/* Pages publiques */}
          <Route path="/cgu" element={<Terms />} />
          <Route path="/confidentialite" element={<Privacy />} />
          <Route path="/suppression-compte" element={<AccountDeletion />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Page login (par défaut) */}
          <Route
            path="*"
            element={
              <div style={{ textAlign: "center", padding: "calc(20px + env(safe-area-inset-top, 0px)) 20px 20px" }}>
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "2rem",
                    color: "#222",
                  }}
                >
                  <h1
                    style={{
                      fontSize: "2.2rem",
                      fontWeight: "700",
                      background: "linear-gradient(90deg, var(--primary-blue), var(--blue-accent))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {t('app.welcome.title')}
                  </h1>
                  <p style={{ fontSize: "1.05rem", color: "#555", marginTop: "0.5rem" }}>
                    {t('app.welcome.subtitle')}
                  </p>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginTop: "20px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                  }}
                >
                  <AuthForm onSuccess={handleAuthSuccess} />

                  {/* (Optionnel) on enlève le disclaimer ici si tu veux */}
                  {/* <div className="auth-disclaimer auth-disclaimer--compact">...</div> */}
                </div>
              </div>
            }
          />
        </Routes>
      ) : (
        <>
          <AppContent
            user={user}
            handleLogout={handleLogout}
            filterDate={filterDate}
            handleFilterChange={handleFilterChange}
          />
          <PushPrimer />
        </>
      )}
    </div>
  );

}

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}
